import { GetAccountInfoUserResponse } from "firebase-admin/lib/auth/user-record";
import { useQuery } from "react-query";
import useUserIdToken from "./useUserIdToken";

export const MFA_INFO_QUERY_KEY = "mfa-info";

const lookupAccountInfo = async (idToken: string) => {
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${process.env.REACT_APP_FIREBASE_APIKEY}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ idToken }),
    },
  );
  const data = await res.json();
  return (data?.users?.[0] as GetAccountInfoUserResponse) ?? null;
};

/**
 * Looks up the current user's account info via the Identity Toolkit API and
 * exposes their enrolled MFA factors. The Firebase client `User` object does
 * not expose `multiFactor.enrolledFactors`, so this lookup is the only way to
 * know on the client whether the user already has MFA enabled.
 *
 * Enrolling/unenrolling a factor is not reflected on the client automatically;
 * invalidate the `MFA_INFO_QUERY_KEY` query to refresh consumers afterward.
 */
export default function useMfaInfo() {
  const idToken = useUserIdToken();

  const { data: user, isLoading } = useQuery({
    enabled: !!idToken,
    queryKey: [MFA_INFO_QUERY_KEY, idToken],
    queryFn: () => lookupAccountInfo(idToken),
    keepPreviousData: true,
  });

  const isMfaEnabled = (user?.mfaInfo?.length ?? 0) > 0;

  return {
    user,
    mfaInfo: user?.mfaInfo,
    isMfaEnabled,
    loading: !idToken || isLoading,
  };
}
